import { isAfter, isSameDay, startOfDay } from 'date-fns';

import { TTransport } from '@/types/models';
import { parseDate, formatDate } from '@/utils/date';
import { TRANSPORT_EVENTS_UUIDS, TRACKING_CASES } from './constants';

const getDatePart = (value: string) => value.split('T')[0];

const isSameCalendarDay = (
  dateString: string | undefined,
  reference: Date = new Date()
) => {
  if (!dateString) return false;

  return isSameDay(parseDate(getDatePart(dateString)), reference);
};

const hasInterval = (interval: unknown) => {
  if (interval == null) return false;
  if (typeof interval === 'string') return interval.trim().length > 0;
  if (typeof interval === 'object') {
    return Object.keys(interval as object).length > 0;
  }

  return Boolean(interval);
};

const getLatestEventUuid = (events: TTransport['events']) => {
  if (!events.length) return undefined;

  const sortedEvents = [...events].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return sortedEvents[0]?.event.uuid;
};

const POSTPONE_EVENT_UUIDS = [
  TRANSPORT_EVENTS_UUIDS.pickupPostponed,
  TRANSPORT_EVENTS_UUIDS.deliveryPostponed,
];

export const caseSelector = (transport_data: TTransport | null) => {
  if (!transport_data) return null;

  const events = transport_data.events;
  const lastEvent = getLatestEventUuid(events);
  const today = startOfDay(new Date());

  const isRegistered =
    events.length === 1 &&
    events[0].event.uuid === TRANSPORT_EVENTS_UUIDS.registered;

  const pickupDate = transport_data.pickup_date
    ? parseDate(getDatePart(transport_data.pickup_date))
    : null;

  // --- cancelled ---
  if (events.some((e) => e.event.uuid === TRANSPORT_EVENTS_UUIDS.cancel)) {
    return TRACKING_CASES.cancelled;
  }

  // --- delivered ---
  if (lastEvent === TRANSPORT_EVENTS_UUIDS.delivered) {
    return TRACKING_CASES.delivered;
  }

  // --- postponed ---
  if (lastEvent && POSTPONE_EVENT_UUIDS.includes(lastEvent)) {
    return TRACKING_CASES.postponed;
  }

  // --- registered ---
  if (isRegistered && pickupDate && isAfter(pickupDate, today)) {
    return TRACKING_CASES.registered;
  }

  // --- out for pickup / intervalPickup ---
  if (
    isRegistered &&
    transport_data.pickup_date &&
    isSameCalendarDay(transport_data.pickup_date)
  ) {
    return hasInterval(transport_data.interval)
      ? TRACKING_CASES.intervalPickup
      : TRACKING_CASES.outForPickup;
  }

  // --- intervalDelivery (Arriving Soon for delivery) ---
  if (
    lastEvent === TRANSPORT_EVENTS_UUIDS.departureFromDepot &&
    isSameCalendarDay(transport_data.delivery_date) &&
    hasInterval(transport_data.interval)
  ) {
    return TRACKING_CASES.intervalDelivery;
  }

  // --- out for delivery ---
  if (
    lastEvent === TRANSPORT_EVENTS_UUIDS.departureFromDepot &&
    isSameCalendarDay(transport_data.delivery_date) &&
    !hasInterval(transport_data.interval)
  ) {
    return TRACKING_CASES.outForDelivery;
  }

  // --- direct last event matches ---
  if (lastEvent === TRANSPORT_EVENTS_UUIDS.pickedUp) {
    return TRACKING_CASES.pickedUp;
  }

  if (lastEvent === TRANSPORT_EVENTS_UUIDS.arrivalAtDepot) {
    return TRACKING_CASES.arrivalAtDepot;
  }

  if (lastEvent === TRANSPORT_EVENTS_UUIDS.departureFromDepot) {
    return TRACKING_CASES.departureFromDepot;
  }

  if (lastEvent === TRANSPORT_EVENTS_UUIDS.goingToLinkoping) {
    return TRACKING_CASES.goingToLinkoping;
  }

  return TRACKING_CASES.notFound;
};
